import React, { useState } from 'react';
import {
  Drawer,
  Box,
  Typography,
  IconButton,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Avatar,
  Button,
  Divider,
  TextField,
  Card,
  CardContent,
  useTheme,
  useMediaQuery,
  Badge,
  Fab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import {
  Close as CloseIcon,
  Add as AddIcon,
  Remove as RemoveIcon,
  Delete as DeleteIcon,
  ShoppingCart as CartIcon,
  Payment as PaymentIcon,
  ShoppingBag as ShoppingBagIcon
} from '@mui/icons-material';
import { useCart } from '../contexts/CartContext';

const Cart = ({ open, onClose, onCheckout }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [confirmDelete, setConfirmDelete] = useState(null);
  
  const { 
    items, 
    totalItems, 
    totalPrice, 
    updateQuantity, 
    removeFromCart, 
    clearCart 
  } = useCart();

  const handleQuantityChange = (itemId, newQuantity) => {
    if (newQuantity <= 0) {
      setConfirmDelete(itemId);
    } else {
      updateQuantity(itemId, newQuantity);
    }
  };

  const handleDeleteConfirm = () => {
    if (confirmDelete) {
      removeFromCart(confirmDelete);
      setConfirmDelete(null);
    }
  };

  const handleDeleteCancel = () => {
    setConfirmDelete(null);
  };

  const CartContent = () => (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <Box 
        sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'space-between',
          p: 2,
          borderBottom: 1,
          borderColor: 'divider'
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <ShoppingBagIcon color="primary" />
          <Typography variant="h6" fontWeight="bold">
            Mon Panier
          </Typography>
          {totalItems > 0 && (
            <Badge badgeContent={totalItems} color="primary" />
          )}
        </Box>
        <IconButton onClick={onClose} size="large">
          <CloseIcon />
        </IconButton>
      </Box>

      {/* Contenu du panier */}
      <Box sx={{ flex: 1, overflow: 'auto' }}>
        {items.length === 0 ? (
          // Panier vide
          <Box 
            sx={{ 
              display: 'flex', 
              flexDirection: 'column', 
              alignItems: 'center', 
              justifyContent: 'center',
              height: '100%',
              p: 3,
              textAlign: 'center'
            }}
          >
            <ShoppingBagIcon 
              sx={{ fontSize: 80, color: 'text.disabled', mb: 2 }} 
            />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              Votre panier est vide
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Découvrez nos produits frais et ajoutez-les à votre panier
            </Typography>
            <Button 
              variant="contained" 
              onClick={onClose}
              size={isMobile ? 'large' : 'medium'}
            >
              Continuer mes achats
            </Button>
          </Box>
        ) : (
          // Liste des articles
          <List sx={{ p: 0 }}>
            {items.map((item, index) => (
              <React.Fragment key={item.id}>
                <ListItem 
                  sx={{ 
                    py: 2, 
                    px: 2,
                    flexDirection: isMobile ? 'column' : 'row',
                    alignItems: isMobile ? 'stretch' : 'center'
                  }}
                >
                  {/* Image et infos produit */}
                  <Box 
                    sx={{ 
                      display: 'flex', 
                      alignItems: 'center',
                      width: isMobile ? '100%' : 'auto',
                      mb: isMobile ? 1 : 0
                    }}
                  >
                    <ListItemAvatar>
                      <Avatar 
                        src={item.image} 
                        alt={item.title}
                        sx={{ width: 60, height: 60 }}
                        variant="rounded"
                      />
                    </ListItemAvatar>
                    
                    <ListItemText
                      primary={
                        <Typography variant="subtitle1" fontWeight="medium">
                          {item.title}
                        </Typography>
                      }
                      secondary={
                        <Box>
                          <Typography variant="body2" color="text.secondary">
                            {item.farmerName} • {item.location}
                          </Typography>
                          <Typography variant="body2" color="primary" fontWeight="medium">
                            {item.price?.toLocaleString()} FCFA / {item.unit}
                          </Typography>
                        </Box>
                      }
                      sx={{ ml: 1 }}
                    />
                  </Box>

                  {/* Contrôles quantité et prix */}
                  <Box 
                    sx={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: isMobile ? 'space-between' : 'flex-end',
                      gap: 2,
                      width: isMobile ? '100%' : 'auto'
                    }}
                  >
                    {/* Contrôles de quantité */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <IconButton 
                        size="small"
                        onClick={() => handleQuantityChange(item.id, item.quantity - 1)}
                      >
                        <RemoveIcon fontSize="small" />
                      </IconButton>
                      
                      <TextField
                        value={item.quantity}
                        onChange={(e) => {
                          const value = parseInt(e.target.value) || 1;
                          handleQuantityChange(item.id, value);
                        }}
                        type="number"
                        inputProps={{ 
                          min: 1,
                          style: { textAlign: 'center', width: '40px' }
                        }}
                        size="small"
                        sx={{ width: 60 }}
                      />
                      
                      <IconButton 
                        size="small"
                        onClick={() => handleQuantityChange(item.id, item.quantity + 1)}
                      >
                        <AddIcon fontSize="small" />
                      </IconButton>
                    </Box>

                    {/* Prix total de l'article */}
                    <Box sx={{ textAlign: 'right', minWidth: 80 }}>
                      <Typography variant="subtitle2" fontWeight="bold">
                        {(item.price * item.quantity).toLocaleString()} FCFA
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {item.quantity} {item.unit}
                      </Typography>
                    </Box>

                    {/* Bouton supprimer */}
                    <IconButton 
                      size="small" 
                      color="error"
                      onClick={() => setConfirmDelete(item.id)}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Box>
                </ListItem>
                
                {index < items.length - 1 && <Divider />}
              </React.Fragment>
            ))}
          </List>
        )}
      </Box>

      {/* Footer avec total et actions */}
      {items.length > 0 && (
        <Box sx={{ borderTop: 1, borderColor: 'divider' }}>
          {/* Résumé du total */}
          <Card sx={{ m: 2, mb: 1 }} elevation={0} variant="outlined">
            <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body2">
                  Articles ({totalItems})
                </Typography>
                <Typography variant="body2">
                  {totalPrice.toLocaleString()} FCFA
                </Typography>
              </Box>
              
              <Divider sx={{ my: 1 }} />
              
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="h6" fontWeight="bold">
                  Total
                </Typography>
                <Typography variant="h6" fontWeight="bold" color="primary">
                  {totalPrice.toLocaleString()} FCFA
                </Typography>
              </Box>
            </CardContent>
          </Card>

          {/* Boutons d'action */}
          <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Button
              variant="contained"
              size={isMobile ? 'large' : 'medium'}
              startIcon={<PaymentIcon />}
              onClick={onCheckout}
              fullWidth
            >
              Payer ma commande
            </Button>
            
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                variant="outlined"
                size={isMobile ? 'large' : 'medium'}
                onClick={onClose}
                fullWidth
              >
                Continuer mes achats
              </Button>
              
              <Button
                variant="text"
                size={isMobile ? 'large' : 'medium'}
                color="error"
                onClick={clearCart}
              >
                Vider
              </Button>
            </Box>
          </Box>
        </Box>
      )}
    </Box>
  );

  return (
    <>
      {/* Drawer pour le panier */}
      <Drawer
        anchor="right"
        open={open}
        onClose={onClose}
        PaperProps={{
          sx: {
            width: isMobile ? '100%' : 450,
            maxWidth: '100vw'
          }
        }}
      >
        <CartContent />
      </Drawer>

      {/* Dialog de confirmation de suppression */}
      <Dialog
        open={!!confirmDelete}
        onClose={handleDeleteCancel}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>
          Supprimer l'article ?
        </DialogTitle>
        <DialogContent>
          <Typography>
            Êtes-vous sûr de vouloir supprimer cet article de votre panier ?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel}>
            Annuler
          </Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained">
            Supprimer
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

// Composant FAB pour ouvrir le panier (à utiliser dans l'app principale)
export const CartFab = ({ onClick }) => {
  const { totalItems } = useCart();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  if (totalItems === 0) return null;

  return (
    <Fab
      color="primary"
      onClick={onClick}
      sx={{
        position: 'fixed',
        bottom: isMobile ? 16 : 24,
        right: isMobile ? 16 : 24,
        zIndex: 1000
      }}
    >
      <Badge badgeContent={totalItems} color="error">
        <CartIcon />
      </Badge>
    </Fab>
  );
};

export default Cart;